/**
 * AlertContext
 * 
 * React Context provider for managing alert state throughout the application.
 * Provides state management for alerts and notifications, methods for CRUD operations,
 * and event subscription for real-time updates.
 * 
 * @module AlertContext
 */
import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import AlertService from '../services/AlertService';
import EventListenerManager from '../alerts/eventListeners/EventListenerManager'; // Added import
import { createAlert, validateAlert } from '../models/AlertModels';
import NotificationService from '../services/NotificationService';
import { getCurrentUser, addUserInfoToObject } from '../utils/AuthUtils';

// Import services

// Initialize services
const alertService = new AlertService();

// Initialize EventListenerManager
const eventListenerManager = new EventListenerManager(alertService);
eventListenerManager.initialize().catch(err => {
  console.error('Failed to initialize EventListenerManager in AlertContext:', err);
  // Optionally, set an error state here or dispatch an error action
});

// Create NotificationService instance
const notificationService = new NotificationService();

/**
 * Initial state for the alert context
 */
const initialState = {
  alerts: [],
  notifications: [],
  unreadCount: 0,
  isLoading: true,
  error: null,
  formState: {
    currentStep: 0,
    alert: null,
    isEditing: false,
    validationErrors: {}
  }
};

/**
 * Action types for the reducer
 */
const ActionTypes = {
  SET_ALERTS: 'SET_ALERTS',
  ADD_ALERT: 'ADD_ALERT',
  UPDATE_ALERT: 'UPDATE_ALERT',
  DELETE_ALERT: 'DELETE_ALERT',
  SET_NOTIFICATIONS: 'SET_NOTIFICATIONS',
  ADD_NOTIFICATION: 'ADD_NOTIFICATION',
  UPDATE_NOTIFICATION: 'UPDATE_NOTIFICATION',
  DELETE_NOTIFICATION: 'DELETE_NOTIFICATION',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_FORM_STATE: 'SET_FORM_STATE',
  RESET_FORM_STATE: 'RESET_FORM_STATE'
};

/**
 * Reducer function for managing alert context state
 * @param {Object} state - Current state
 * @param {Object} action - Action to perform
 * @returns {Object} - New state
 */
function alertReducer(state, action) {
  switch (action.type) {
    case ActionTypes.SET_ALERTS:
      return { ...state, alerts: action.payload, isLoading: false };
    
    case ActionTypes.ADD_ALERT:
      return { ...state, alerts: [...state.alerts, action.payload] };
    
    case ActionTypes.UPDATE_ALERT:
      return {
        ...state,
        alerts: state.alerts.map(alert => 
          alert.id === action.payload.id ? action.payload : alert
        )
      };
    
    case ActionTypes.DELETE_ALERT:
      return {
        ...state,
        alerts: state.alerts.filter(alert => alert.id !== action.payload)
      };
    
    case ActionTypes.SET_NOTIFICATIONS:
      return {
        ...state,
        notifications: action.payload,
        unreadCount: action.payload.filter(n => !n.read).length
      };
    
    case ActionTypes.ADD_NOTIFICATION:
      return {
        ...state,
        notifications: [action.payload, ...state.notifications],
        unreadCount: state.unreadCount + (action.payload.read ? 0 : 1)
      };
    
    case ActionTypes.UPDATE_NOTIFICATION:
      return {
        ...state,
        notifications: state.notifications.map(notification => 
          notification.id === action.payload.id ? action.payload : notification
        ),
        unreadCount: state.notifications
          .map(n => n.id === action.payload.id ? action.payload : n)
          .filter(n => !n.read).length
      };
    
    case ActionTypes.DELETE_NOTIFICATION:
      const deletedNotification = state.notifications.find(n => n.id === action.payload);
      return {
        ...state,
        notifications: state.notifications.filter(n => n.id !== action.payload),
        unreadCount: state.unreadCount - (deletedNotification && !deletedNotification.read ? 1 : 0)
      };
    
    case ActionTypes.SET_LOADING:
      return { ...state, isLoading: action.payload };
    
    case ActionTypes.SET_ERROR:
      return { ...state, error: action.payload, isLoading: false };
    
    case ActionTypes.SET_FORM_STATE:
      return {
        ...state,
        formState: { ...state.formState, ...action.payload }
      };
    
    case ActionTypes.RESET_FORM_STATE:
      return {
        ...state,
        formState: {
          currentStep: 0,
          alert: null,
          isEditing: false,
          validationErrors: {}
        }
      };
    
    default:
      return state;
  }
}

// Create the context
const AlertContext = createContext();

/**
 * AlertProvider component
 * @param {Object} props - Component props
 * @returns {JSX.Element} - Provider component
 */
export function AlertProvider({ children }) {
  const [state, dispatch] = useReducer(alertReducer, initialState);
  
  /**
   * Load alerts from the service
   */
  const loadAlerts = useCallback(async () => {
    dispatch({ type: ActionTypes.SET_LOADING, payload: true });
    try {
      const alerts = await alertService.getUserAlerts();
      dispatch({ type: ActionTypes.SET_ALERTS, payload: alerts });
    } catch (error) {
      console.error('Failed to load alerts:', error);
      dispatch({ type: ActionTypes.SET_ERROR, payload: error.message });
    }
  }, []);
  
  /**
   * Load notifications from the service
   */
  const loadNotifications = useCallback(async () => {
    try {
      const notifications = await notificationService.getUserNotifications();
      dispatch({ type: ActionTypes.SET_NOTIFICATIONS, payload: notifications });
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  }, []);
  
  /**
   * Create a new alert
   * @param {Object} alertData - Alert data
   * @returns {Promise<Object>} - Created alert
   */
  const createNewAlert = async (alertData) => {
    console.log('-------------------- DEBUG: CREATE ALERT START --------------------');
    console.log('Alert data received:', alertData);
    
    try {
      // Get current user information
      const currentUser = getCurrentUser();
      console.log('Current user from AuthUtils:', currentUser);
      
      if (!currentUser) {
        console.error('No authenticated user found. Cannot create alert without user context');
        dispatch({ 
          type: ActionTypes.SET_ERROR, 
          payload: 'Authentication required to create alerts' 
        });
        return null;
      }
      
      // Generate a UUID for the alert
      const alertWithId = { ...alertData, id: uuidv4() };
      console.log('Alert with ID:', alertWithId);
      
      // Add user information to the alert
      alertWithId.user_id = currentUser.user_id;
      alertWithId.userId = currentUser.user_id; // Include both formats for compatibility
      alertWithId.userName = currentUser.user_name || 'Unknown User';
      console.log('Alert with user info:', alertWithId);
      
      // Add timestamp information if not present
      if (!alertWithId.createdAt) {
        alertWithId.createdAt = Date.now();
      }
      if (!alertWithId.updatedAt) {
        alertWithId.updatedAt = Date.now();
      }
      
      // Validate the alert
      console.log('Validating alert...');
      const validation = validateAlert(alertWithId);
      console.log('Validation result:', validation);
      
      if (!validation.isValid) {
        console.error('Alert validation failed:', validation.errors);
        dispatch({
          type: ActionTypes.SET_FORM_STATE,
          payload: { validationErrors: validation.errors }
        });
        return null;
      }
      
      // Create the alert in the database
      console.log('Creating alert in database...');
      const result = await alertService.createAlert(alertWithId);
      console.log('Alert creation result:', result);
      
      // Verify alert was added to the database
      console.log('Verifying alert in database...');
      try {
        const createdAlert = await alertService.repository.get(alertWithId.id);
        console.log('Alert found in database:', createdAlert);
      } catch (verifyError) {
        console.error('Error verifying alert in database:', verifyError);
      }
      
      // Update the state
      console.log('Updating state with new alert...');
      dispatch({ type: ActionTypes.ADD_ALERT, payload: alertWithId });
      
      // Reload alerts to ensure state is synchronized with database
      console.log('Reloading all alerts...');
      loadAlerts();
      
      console.log('-------------------- DEBUG: CREATE ALERT END --------------------');
      return alertWithId;
    } catch (error) {
      console.error('Failed to create alert:', error);
      console.error('Error stack:', error.stack);
      dispatch({ type: ActionTypes.SET_ERROR, payload: error.message });
      console.log('-------------------- DEBUG: CREATE ALERT ERROR END --------------------');
      return null;
    }
  };
  
  /**
   * Update an existing alert
   * @param {string} alertId - ID of the alert to update
   * @param {Object} updates - Updates to apply
   * @returns {Promise<Object>} - Updated alert
   */
  const updateExistingAlert = async (alertId, updates) => {
    try {
      // Get the current alert
      const currentAlert = state.alerts.find(a => a.id === alertId);
      if (!currentAlert) {
        throw new Error(`Alert with ID ${alertId} not found`);
      }
      
      // Create the updated alert
      const updatedAlert = { ...currentAlert, ...updates };
      
      // Validate the alert
      const validation = validateAlert(updatedAlert);
      if (!validation.isValid) {
        dispatch({
          type: ActionTypes.SET_FORM_STATE,
          payload: { validationErrors: validation.errors }
        });
        return null;
      }
      
      // Update the alert
      await alertService.updateAlert(alertId, updates);
      
      // Update the state
      dispatch({ type: ActionTypes.UPDATE_ALERT, payload: updatedAlert });
      
      return updatedAlert;
    } catch (error) {
      console.error('Failed to update alert:', error);
      dispatch({ type: ActionTypes.SET_ERROR, payload: error.message });
      return null;
    }
  };
  
  /**
   * Delete an alert
   * @param {string} alertId - ID of the alert to delete
   * @returns {Promise<boolean>} - Success status
   */
  const deleteAlert = async (alertId) => {
    try {
      await alertService.deleteAlert(alertId);
      dispatch({ type: ActionTypes.DELETE_ALERT, payload: alertId });
      return true;
    } catch (error) {
      console.error('Failed to delete alert:', error);
      dispatch({ type: ActionTypes.SET_ERROR, payload: error.message });
      return false;
    }
  };
  
  /**
   * Toggle alert enabled status
   * @param {string} alertId - ID of the alert to toggle
   * @returns {Promise<Object>} - Updated alert
   */
  const toggleAlertEnabled = async (alertId) => {
    try {
      const alert = state.alerts.find(a => a.id === alertId);
      if (!alert) {
        throw new Error(`Alert with ID ${alertId} not found`);
      }
      
      return await updateExistingAlert(alertId, { enabled: !alert.enabled });
    } catch (error) {
      console.error('Failed to toggle alert enabled status:', error);
      dispatch({ type: ActionTypes.SET_ERROR, payload: error.message });
      return null;
    }
  };
  
  /**
   * Mark a notification as read
   * @param {string} notificationId - ID of the notification to mark as read
   * @returns {Promise<boolean>} - Success status
   */
  const markNotificationAsRead = async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);
      
      const updatedNotification = {
        ...state.notifications.find(n => n.id === notificationId),
        read: true
      };
      
      dispatch({
        type: ActionTypes.UPDATE_NOTIFICATION,
        payload: updatedNotification
      });
      
      return true;
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      return false;
    }
  };
  
  /**
   * Mark all notifications as read
   * @returns {Promise<boolean>} - Success status
   */
  const markAllNotificationsAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      
      const updatedNotifications = state.notifications.map(n => ({ ...n, read: true }));
      
      dispatch({
        type: ActionTypes.SET_NOTIFICATIONS,
        payload: updatedNotifications
      });
      
      return true;
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      return false;
    }
  };
  
  /**
   * Delete a notification
   * @param {string} notificationId - ID of the notification to delete
   * @returns {Promise<boolean>} - Success status
   */
  const deleteNotification = async (notificationId) => {
    try {
      await notificationService.deleteNotification(notificationId);
      dispatch({ type: ActionTypes.DELETE_NOTIFICATION, payload: notificationId });
      return true;
    } catch (error) {
      console.error('Failed to delete notification:', error);
      return false;
    }
  };
  
  /**
   * Start creating a new alert
   * @param {string} alertType - Type of alert to create
   * @param {Object} [alertData=null] - Optional alert data for pre-configuration
   */
  const startNewAlert = useCallback((alertType, alertData = null) => {

    const defaultProperties = {
      type: alertType, // Initial type from argument
      rules: [],
      actions: ['Notification'], // Default action
      scope: { type: 'global' }, // Default scope
      name: '',
      description: '',
      severity: 'MEDIUM', // Default severity
      meta: {} // Initialize meta as an empty object
    };

    let finalAlertData = { ...defaultProperties };

    if (alertData) {
      // Merge provided alertData, potentially overwriting defaults
      finalAlertData = { ...finalAlertData, ...alertData };

      // Check for process-initiated flags in either root level or meta object
      const isProcessInitiated = alertData.isProcessInitiated || 
                              (alertData.meta && alertData.meta.initiatedFromProcess);
      
      // Special handling for process-initiated alerts
      if (isProcessInitiated) {

        
        // Get target process name from either root or meta
        const targetProcessName = alertData.targetProcessName || 
                               (alertData.meta && alertData.meta.targetProcessName) || '';
        
        // Force type to be Process for process-initiated alerts
        finalAlertData.type = 'Process';
        
        // Make sure meta contains all process-related info
        const processMeta = {
          initiatedFromProcess: true,
          lockAlertType: true,
          lockProcessName: true,
          targetProcessName: targetProcessName,
          // Preserve any other meta properties passed in alertData.meta
          ...(alertData.meta || {})
        };
        

        
        // Merge processMeta into existing meta, ensuring no important flags are lost
        finalAlertData.meta = { ...finalAlertData.meta, ...processMeta };
      } else {
        // For non-process-initiated alerts, ensure meta is properly merged if provided
        finalAlertData.meta = { ...(defaultProperties.meta || {}), ...(alertData.meta || {}) };
      }
    }
    
    // Create the alert object using the merged data
    const newAlert = createAlert(finalAlertData);

    
    dispatch({
      type: ActionTypes.SET_FORM_STATE,
      payload: {
        currentStep: 0,
        alert: newAlert,
        isEditing: false,
        validationErrors: {}
      }
    });
  }, []);
  
  /**
   * Start editing an existing alert or reset the form state
   * @param {string|null} alertId - ID of the alert to edit, or null to reset
   */
  const startEditAlert = (alertId) => {
    // If alertId is null, reset the form state
    if (alertId === null) {
      dispatch({
        type: ActionTypes.RESET_FORM_STATE
      });
      return;
    }
    
    // Find the alert to edit
    const alert = state.alerts.find(a => a.id === alertId);
    if (!alert) {
      console.error(`Alert with ID ${alertId} not found`);
      // Reset form state to prevent issues
      dispatch({
        type: ActionTypes.RESET_FORM_STATE
      });
      return;
    }
    
    dispatch({
      type: ActionTypes.SET_FORM_STATE,
      payload: {
        currentStep: 0,
        alert,
        isEditing: true,
        validationErrors: {}
      }
    });
  };
  
  /**
   * Update the form state
   * @param {Object} updates - Updates to apply to the form state
   */
  const updateFormState = (updates) => {
    dispatch({
      type: ActionTypes.SET_FORM_STATE,
      payload: updates
    });
  };
  
  /**
   * Reset the form state
   */
  const resetFormState = () => {
    dispatch({ type: ActionTypes.RESET_FORM_STATE });
  };
  
  /**
   * Process incoming events for alert evaluation
   * @param {Object} event - Event to evaluate
   * @param {string} eventType - Type of event
   * @param {string} [serverId] - Optional server ID
   */
  const processEvent = useCallback(async (event, eventType, serverId = null) => {
    try {
      // Evaluate the event against all applicable alerts
      const triggeredAlerts = await alertService.evaluateEvent(event, eventType, serverId);
      
      if (triggeredAlerts.length > 0) {
        // Create notifications for triggered alerts
        const notifications = alertService.createNotifications(
          triggeredAlerts,
          event,
          eventType
        );
        
        // Save notifications and update state
        for (const notification of notifications) {
          try {
            const savedNotification = await notificationService.createNotification(notification);
            dispatch({
              type: ActionTypes.ADD_NOTIFICATION,
              payload: savedNotification
            });
          } catch (error) {
            console.error('Failed to create notification:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error processing event:', error);
    }
  }, []);
  
  // Request notification permissions on mount
  useEffect(() => {
    const requestNotificationPermission = async () => {
      try {
        const permission = await notificationService.requestNotificationPermission();
        console.log('Notification permission status:', permission);
      } catch (error) {
        console.error('Error requesting notification permission:', error);
      }
    };
    
    requestNotificationPermission();
  }, []);

  // Load alerts and notifications on mount
  useEffect(() => {
    loadAlerts();
    loadNotifications();
  }, [loadAlerts, loadNotifications]);
  
  // Subscribe to WebSocket events for real-time updates
  useEffect(() => {
    // This will be implemented when we have WebSocket context integration
    // For now, just a placeholder
    const subscribeToEvents = () => {
      console.log('Subscribing to events for alert evaluation...');
      // In a real implementation, we would subscribe to WebSocket events here
    };
    
    subscribeToEvents();
    
    // Return cleanup function
    return () => {
      console.log('Unsubscribing from events...');
      // In a real implementation, we would unsubscribe from WebSocket events here
    };
  }, [processEvent]);

  // One-time cleanup of legacy/invalid alerts at startup for DB hygiene
  useEffect(() => {
    // This effect runs only once when the AlertProvider mounts.
    // It ensures any legacy or invalid alerts (e.g., with old rule condition formats) are removed.
    // This prevents repeated log spam and unnecessary DB writes during normal operation.
    // For more info, see the comments in AlertService.getUserAlerts().
    const cleanupInvalidAlerts = async () => {
      try {
        await alertService.removeInvalidAlerts();
        console.log('[AlertContext] Completed one-time invalid alert cleanup');
      } catch (err) {
        console.error('[AlertContext] Error during invalid alert cleanup:', err);
      }
    };
    cleanupInvalidAlerts();
  }, []);

  // Subscribe to notification updates from other components/tabs
  useEffect(() => {
    const notificationUpdateChannel = new BroadcastChannel('notification-updates');

    const handleNotificationUpdate = (event) => {
      console.log('AlertContext: Received notification update via BroadcastChannel:', event.data);
      // Reload notifications to update the list and unread count
      // This will handle new notifications, read status changes, and deletions (including clear all)
      loadNotifications();
    };

    notificationUpdateChannel.addEventListener('message', handleNotificationUpdate);

    // Clean up the event listener and close the channel on unmount
    return () => {
      notificationUpdateChannel.removeEventListener('message', handleNotificationUpdate);
      notificationUpdateChannel.close();
      console.log('AlertContext: Unsubscribed from notification-updates BroadcastChannel.');
    };
  }, [loadNotifications]); // Dependency: loadNotifications
  
  // Create the context value
  const contextValue = {
    ...state,
    createAlert: createNewAlert,
    updateAlert: updateExistingAlert,
    deleteAlert,
    toggleAlertEnabled,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    deleteNotification,
    startNewAlert,
    startEditAlert,
    updateFormState,
    resetFormState,
    processEvent,
    refreshAlerts: loadAlerts,
    refreshNotifications: loadNotifications
  };
  
  return (
    <AlertContext.Provider value={contextValue}>
      {children}
    </AlertContext.Provider>
  );
}

/**
 * Custom hook for using the alert context
 * @returns {Object} - Alert context value
 */
export function useAlertContext() {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlertContext must be used within an AlertProvider');
  }
  return context;
}

export default AlertContext;
